import { createMemory, searchMemories } from '@4cm/brain/brainMemory';
import { isPlaceholderSupabase } from './supabase';
import { appendLocalGrowthCheckin, loadLocalGrowthCheckins } from './demoData';

/**
 * Coach growth check-in (Layer 3 surface).
 *
 * A short conversational reflection — 4 questions, ~3 minutes — written into
 * `brain_memories` tagged `growth-checkin` so future Brain turns retrieve it
 * as a prior. The Layer 2 → Layer 3 → Layer 2 loop closes via Brain's existing
 * `searchMemories` call inside `buildBrainContext`.
 */

export interface GrowthQuestion {
  id: string;
  prompt: string;
  hint: string;
}

export const GROWTH_QUESTIONS: GrowthQuestion[] = [
  {
    id: 'wins',
    prompt: 'Step back from the last fortnight — what went better than you expected?',
    hint: 'one specific moment is enough',
  },
  {
    id: 'misses',
    prompt: 'What didn\'t land? A session that fell flat, a player you couldn\'t reach, a decision you\'d take back.',
    hint: 'name it plainly',
  },
  {
    id: 'wrestling',
    prompt: 'What is your team wrestling with right now?',
    hint: 'a recurring theme — defending, focus, communication',
  },
  {
    id: 'help',
    prompt: 'What\'s one specific thing you\'d like Brain to help with this fortnight?',
    hint: 'this becomes a prior on every Brain turn for the next two weeks',
  },
];

export interface GrowthCheckinAnswer {
  questionId: string;
  prompt: string;
  answer: string;
}

export interface GrowthCheckinState {
  step: number;
  answers: GrowthCheckinAnswer[];
  finished: boolean;
}

export function createInitialGrowthCheckin(): GrowthCheckinState {
  return { step: 0, answers: [], finished: false };
}

export function advanceGrowthCheckin(state: GrowthCheckinState, answer: string): GrowthCheckinState {
  const clean = answer.trim();
  if (!clean) return state;
  const question = GROWTH_QUESTIONS[state.step];
  if (!question) return state;
  const nextAnswers = [...state.answers, { questionId: question.id, prompt: question.prompt, answer: clean }];
  const nextStep = state.step + 1;
  return {
    step: nextStep,
    answers: nextAnswers,
    finished: nextStep >= GROWTH_QUESTIONS.length,
  };
}

export function currentGrowthQuestion(state: GrowthCheckinState): GrowthQuestion | null {
  return GROWTH_QUESTIONS[state.step] ?? null;
}

export interface GrowthCheckinSummary {
  id: string;
  fact: string;
  created_at: string;
  answers: GrowthCheckinAnswer[];
}

interface SaveOptions {
  coachId: string;
  teamId: string | null;
  answers: GrowthCheckinAnswer[];
}

/**
 * Persist a growth check-in as a Brain memory.
 * The fact is a compact summary; the full answers go in tags + a structured field
 * via the memory record's tags array. Brain retrieves these on every turn.
 */
export async function saveGrowthCheckin(opts: SaveOptions): Promise<{ ok: true; memoryId: string } | { ok: false; error: string }> {
  const { coachId, teamId, answers } = opts;
  const wins = answers.find((a) => a.questionId === 'wins')?.answer ?? '';
  const misses = answers.find((a) => a.questionId === 'misses')?.answer ?? '';
  const wrestling = answers.find((a) => a.questionId === 'wrestling')?.answer ?? '';
  const help = answers.find((a) => a.questionId === 'help')?.answer ?? '';

  const fact = [
    `Coach growth check-in (${new Date().toLocaleDateString()}):`,
    `• Wins: ${wins}`,
    `• Misses: ${misses}`,
    `• Team is wrestling with: ${wrestling}`,
    `• Wants help with: ${help}`,
  ].join('\n');
  const tags = ['growth-checkin', 'layer3', 'fortnightly'];

  // Offline path — Brain memory writes can't reach Supabase, so the
  // check-in lands in localStorage. Brain's offline synthesizer reads
  // from this same store to weave the prior into its next reply,
  // closing the Layer 3 → Layer 2 loop end-to-end.
  if (isPlaceholderSupabase()) {
    try {
      const row = appendLocalGrowthCheckin({
        coach_id: coachId,
        team_id: teamId,
        fact,
        answers,
        tags,
      });
      return { ok: true, memoryId: row.id };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not save check-in locally.' };
    }
  }

  const memory = await createMemory({
    coach_id: coachId,
    team_id: teamId,
    kind: 'reflection',
    fact,
    tags,
    confidence: 0.9,
    scope: 'coach',
  });

  if (!memory) return { ok: false, error: 'Could not save growth check-in.' };
  return { ok: true, memoryId: memory.id };
}

export async function listRecentGrowthCheckins(coachId: string, limit = 10): Promise<GrowthCheckinSummary[]> {
  if (isPlaceholderSupabase()) {
    return loadLocalGrowthCheckins(coachId, limit).map((c) => ({
      id: c.id,
      fact: c.fact,
      created_at: c.created_at,
      answers: c.answers,
    }));
  }

  const memories = await searchMemories({
    coach_id: coachId,
    team_id: null,
    query: 'growth check-in',
    tags: ['growth-checkin'],
    limit,
  });
  return memories.map((m) => ({
    id: m.id,
    fact: m.fact,
    created_at: m.created_at,
    answers: [],
  }));
}
